import { ReactNode, useRef, useState } from 'react'
import styled from 'styled-components'
import { useDetectOutsideClick } from '../../hooks/useDetectOutsideClick'
import FilterIcon from '../../icons/IconLib/FilterIcon'
import KbdChipButton from '../KbdChipButton/KbdChipButton'
import { FormShell, FormGrid } from '../FormLayout'

export interface FilterPopoverProps {
    /** aria-label / title of the trigger and the popover dialog. */
    label: string
    /** Active-filter counter badge on the trigger (rendered when > 0). */
    count?: number
    /** Accent trigger; defaults to `count > 0`. */
    active?: boolean
    /** Trigger icon; defaults to FilterIcon. */
    icon?: ReactNode
    /** Top zone: action buttons (KbdChipButton etc.). */
    actions?: ReactNode
    /** Bottom zone: filter fields (FormField items), laid out in FormShell / FormGrid. */
    children?: ReactNode
}

/**
 * Toolbar filter chip with a zoned popover: action buttons on top, filter selectors below,
 * a divider between them. Closes on outside click only, so several filters can be set in a row.
 */
export const FilterPopover = ({ label, count = 0, active, icon, actions, children }: FilterPopoverProps) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const [open, setOpen] = useState(false)

    useDetectOutsideClick(wrapperRef, () => setOpen(false))

    const isActive = active ?? count > 0

    return (
        <Wrapper ref={wrapperRef}>
            <Trigger
                type='button'
                variant={isActive ? 'accent' : 'default'}
                count={count}
                aria-haspopup='dialog'
                aria-expanded={open}
                aria-label={label}
                title={label}
                onClick={() => setOpen((v) => !v)}
            >
                {icon ?? <FilterIcon color='currentColor' />}
            </Trigger>

            {open && (
                <Panel role='dialog' aria-label={label}>
                    {actions && <Actions>{actions}</Actions>}
                    {actions && children && <Divider />}
                    {children && (
                        <FormShell>
                            <FormGrid>{children}</FormGrid>
                        </FormShell>
                    )}
                </Panel>
            )}
        </Wrapper>
    )
}

export default FilterPopover

const Wrapper = styled.div`
    position: relative;
    flex-shrink: 0;
`

/* Same size as SearchInput / DatePicker, so it lines up in a toolbar. */
const Trigger = styled(KbdChipButton)`
    height: 2.875rem;
    min-width: 2.875rem;
    border-radius: 0.875rem;

    svg {
        width: 1.25rem;
        height: 1.25rem;
    }
`

const Panel = styled.div`
    position: absolute;
    top: calc(100% + 0.5rem);
    inset-inline-end: 0;
    z-index: 30;
    width: 18rem;
    max-width: calc(100vw - 1.5rem);
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1rem;
    box-shadow: ${({ theme }) => theme.shadows.card};
    padding: 0.875rem;
    direction: rtl;
`

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`

const Divider = styled.hr`
    margin: 0.75rem 0;
    border: 0;
    border-top: 1px solid ${({ theme }) => theme.colors.line};
`
